import type { LucideIcon } from "lucide-react"
import {
  ActivityIcon,
  Building2Icon,
  FileTextIcon,
  HouseIcon,
  LeafIcon,
  ShieldIcon,
  TruckIcon,
  UserRoundIcon,
} from "lucide-react"

export interface OperatorShellNavigationChild {
  description: string
  label: string
  matchPrefixes?: readonly string[]
  to: string
}

export interface OperatorShellNavigationItem {
  description: string
  icon: LucideIcon
  items?: readonly OperatorShellNavigationChild[]
  label: string
  matchPrefixes?: readonly string[]
  to: string
}

export interface OperatorShellNavigationItemState extends OperatorShellNavigationItem {
  hasActiveChild: boolean
  isActive: boolean
  items?: readonly (OperatorShellNavigationChild & { isActive: boolean })[]
}

export interface OperatorShellSecondaryNavigationItem {
  description: string
  icon: LucideIcon
  label: string
  matchPrefixes?: readonly string[]
  to: string
}

export interface OperatorShellSecondaryNavigationItemState extends OperatorShellSecondaryNavigationItem {
  isActive: boolean
}

export const operatorPrimaryNavigationScaffold: readonly OperatorShellNavigationItem[] =
  [
    {
      description: "Authority incident desk and jurisdiction reporting flows.",
      icon: ShieldIcon,
      items: [
        {
          description: "Live incident triage and dispatch workflow.",
          label: "Desk",
          matchPrefixes: ["/authority/incidents"],
          to: "/authority",
        },
        {
          description: "Authority reporting and incident export views.",
          label: "Reports",
          matchPrefixes: ["/authority/reports/incidents"],
          to: "/authority/reports",
        },
      ],
      label: "Authority",
      matchPrefixes: ["/authority"],
      to: "/authority",
    },
    {
      description: "Fleet onboarding, master data, kit issuance, analytics, and certification operations.",
      icon: TruckIcon,
      items: [
        {
          description: "Fleet operational entry that resolves or resumes the active onboarding run.",
          label: "Overview",
          to: "/fleet",
        },
        {
          description: "Read-only incident monitoring lane for fleet operators.",
          label: "Incidents",
          matchPrefixes: ["/fleet/incidents"],
          to: "/fleet/incidents",
        },
        {
          description: "Read-only authority roster and fallback-contact directory for Fleet super admins.",
          label: "Authority Directory",
          matchPrefixes: ["/fleet/authority-directory"],
          to: "/fleet/authority-directory",
        },
        {
          description: "Enterprise onboarding organization/company form and first working setup frame.",
          label: "Onboarding",
          matchPrefixes: ["/fleet/onboarding"],
          to: "/fleet/onboarding/organization",
        },
        {
          description: "Branch, depot, hub, and route-base master data.",
          label: "Operating Units",
          matchPrefixes: ["/fleet/operating-units"],
          to: "/fleet/operating-units",
        },
        {
          description: "Truck, bus, and two-wheeler asset roster.",
          label: "Assets",
          matchPrefixes: ["/fleet/assets"],
          to: "/fleet/assets",
        },
        {
          description: "Drivers, helpers, conductors, riders, and supervisors.",
          label: "People",
          matchPrefixes: ["/fleet/people"],
          to: "/fleet/people",
        },
        {
          description: "Assignment history between assets and people.",
          label: "Assignments",
          matchPrefixes: ["/fleet/assignments"],
          to: "/fleet/assignments",
        },
        {
          description: "System-issued kit activation, issuance, and print preparation.",
          label: "Kits",
          matchPrefixes: ["/fleet/kits"],
          to: "/fleet/kits",
        },
        {
          description: "Fleet analytics and reporting.",
          label: "Analytics",
          matchPrefixes: ["/fleet/analytics"],
          to: "/fleet/analytics",
        },
        {
          description: "Certification issuance and status tracking.",
          label: "Certifications",
          matchPrefixes: ["/fleet/certifications"],
          to: "/fleet/certifications",
        },
        {
          description: "Issued sticker registry, scan URLs, and bridge QR generation.",
          label: "QR Registry",
          matchPrefixes: ["/fleet/tags"],
          to: "/fleet/tags",
        },
      ],
      label: "Fleet",
      matchPrefixes: ["/fleet"],
      to: "/fleet",
    },
    {
      description: "Orchard intelligence, tree registry, and seasonal yield workflows.",
      icon: LeafIcon,
      items: [
        {
          description: "Orchard overview and module setup.",
          label: "Overview",
          to: "/orchards",
        },
      ],
      label: "Orchards",
      matchPrefixes: ["/orchards"],
      to: "/orchards",
    },
    {
      description: "Portal administration and stock operations.",
      icon: Building2Icon,
      items: [
        {
          description: "Administrative overview and organization controls.",
          label: "Admin Desk",
          to: "/admin",
        },
        {
          description: "Stock generation, print preparation, and batch review.",
          label: "Stock Ops",
          matchPrefixes: ["/admin/stock-ops"],
          to: "/admin/stock-ops",
        },
      ],
      label: "Admin",
      matchPrefixes: ["/admin"],
      to: "/admin",
    },
    {
      description:
        "Operational monitoring, integrations, and partner handoffs.",
      icon: ActivityIcon,
      items: [
        {
          description: "Operational monitoring and alert review.",
          label: "Monitor",
          to: "/ops",
        },
        {
          description: "Vendor connectivity and integration diagnostics.",
          label: "Integrations",
          matchPrefixes: ["/ops/integrations"],
          to: "/ops/integrations",
        },
        {
          description: "Partner delivery operations and outbound exports.",
          label: "Partner Deliveries",
          matchPrefixes: ["/ops/partner-deliveries"],
          to: "/ops/partner-deliveries",
        },
      ],
      label: "Ops",
      matchPrefixes: ["/ops"],
      to: "/ops",
    },
    {
      description: "Export overviews and FNOL production workflow.",
      icon: FileTextIcon,
      items: [
        {
          description: "Export queue overview and operational summary.",
          label: "Overview",
          to: "/exports",
        },
        {
          description: "FNOL desk and export package preparation.",
          label: "FNOL",
          matchPrefixes: ["/exports/fnol"],
          to: "/exports/fnol",
        },
      ],
      label: "Exports",
      matchPrefixes: ["/exports"],
      to: "/exports",
    },
  ]

export const operatorSecondaryNavigationScaffold: readonly OperatorShellSecondaryNavigationItem[] =
  [
    {
      description: "Open the account bridge and authentication settings.",
      icon: UserRoundIcon,
      label: "Account",
      matchPrefixes: ["/account"],
      to: "/account",
    },
    {
      description: "Return to the public-facing foundation surface.",
      icon: HouseIcon,
      label: "Home",
      matchPrefixes: ["/"],
      to: "/",
    },
  ]

function matchesOperatorPath(
  pathname: string,
  to: string,
  matchPrefixes: readonly string[] = [],
  options: {
    exactTo?: boolean
  } = {}
) {
  const { exactTo = false } = options
  const normalizedPath = pathname === "/" ? "/" : pathname.replace(/\/+$/, "")
  const normalizedTo = to === "/" ? "/" : to.replace(/\/+$/, "")

  if (exactTo) {
    if (normalizedPath === normalizedTo) {
      return true
    }
  } else {
    const toMatches =
      normalizedTo === "/"
        ? normalizedPath === "/"
        : normalizedPath === normalizedTo ||
          normalizedPath.startsWith(`${normalizedTo}/`)

    if (toMatches) {
      return true
    }
  }

  return matchPrefixes.some((candidate) => {
    const normalizedCandidate =
      candidate === "/" ? "/" : candidate.replace(/\/+$/, "")

    if (normalizedCandidate === "/") {
      return normalizedPath === "/"
    }

    return (
      normalizedPath === normalizedCandidate ||
      normalizedPath.startsWith(`${normalizedCandidate}/`)
    )
  })
}

export function resolveOperatorPrimaryNavigation(
  pathname: string,
  items: readonly OperatorShellNavigationItem[] = operatorPrimaryNavigationScaffold
): readonly OperatorShellNavigationItemState[] {
  return items.map((item) => {
    const resolvedItems = item.items?.map((child) => ({
      ...child,
      isActive: matchesOperatorPath(pathname, child.to, child.matchPrefixes, {
        exactTo: true,
      }),
    }))
    const hasActiveChild =
      resolvedItems?.some((child) => child.isActive) ?? false

    return {
      ...item,
      hasActiveChild,
      isActive: matchesOperatorPath(pathname, item.to, item.matchPrefixes),
      items: resolvedItems,
    }
  })
}

export function resolveOperatorSecondaryNavigation(
  pathname: string,
  items: readonly OperatorShellSecondaryNavigationItem[] = operatorSecondaryNavigationScaffold
): readonly OperatorShellSecondaryNavigationItemState[] {
  return items.map((item) => ({
    ...item,
    isActive: matchesOperatorPath(pathname, item.to, item.matchPrefixes),
  }))
}
